import React from 'react';
import { Box } from '@updater/ui-uds';
import Header from '../header/Header';
import { TasksSidebar } from '../tasks-sidebar/TasksSidebar';
import { VoiceAgentScreen } from '../../VoiceAgentScreen';
import { GlassEffectBackground } from '../chat-ui/glass-effect-background/GlassEffectBackground';
import { CompactProgressBar } from '../progress-bar/CompactProgressBar';
import styled from '@emotion/styled';
import ChatReadinessGate from '../chat-ui/chat-interface/ChatReadinessGate';
import { ChatColors } from '../../../configs/chat-colors';
import { useVirtualAssistantChatReturn } from 'flows/virtual-building-assistant/hooks/useVirtualAssistantChat';

const Chat = styled.div`
  width: 100%;
  background-color: ${ChatColors.chat.BG};
  height: 100vh;
  position: absolute;
  right: 0;
  top: 0;
  overflow: hidden;
  min-height: 100vh;
  min-width: 100%;
  @media (min-width: 1024px) {
    width: 25%;
    min-width: 650px;
  }
`;
const ChatBackground = styled(Box)`
  position: absolute;
  background-color: ${ChatColors.chat.BG_DARK};
  right: 0;
  width: 100%;
  height: 100vh;
  @media (min-width: 1024px) {
    width: 25%;
    min-width: 650px;
  }
`;
const FullScreenContainer = styled(Box)`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: ${ChatColors.chat.TEXT};
  min-height: 100vh;
  min-width: 100%;
  transform: translateZ(0);
  will-change: auto;
  contain: layout;
  @media (min-width: 1024px) {
    width: 25%;
    min-width: 650px;
  }
`;
const ContentContainer = styled.div`
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 100vh;
  min-width: 100%;
  transform: translateZ(0);
  will-change: auto;
  contain: layout;
`;
const GlassEffect = styled.div`
  position: absolute;
  z-index: 1;
  flex-shrink: 0;
  right: -170px;
  top: -20px;
  @media (min-width: 1024px) {
    right: 100px;
  }
`;

export interface ChatComponentProps {
  uiState: useVirtualAssistantChatReturn['chatComponentProps']['uiState'];
  data: useVirtualAssistantChatReturn['chatComponentProps']['data'];
  handleSidebarClose: () => void;
  handleSelectTask: (task: string) => void;
  stableHandleSendMessageWithTracking: (msg: string, files?: File[]) => void;
  stableOnTypewriterDone: () => void;
  onBack?: () => void;
}

export const ChatComponent = React.memo((props: ChatComponentProps) => {
  const {
    uiState,
    data,
    handleSidebarClose,
    handleSelectTask,
    stableHandleSendMessageWithTracking,
    stableOnTypewriterDone,
    onBack,
  } = props;
  if (!data) return null;
  return (
    <Chat key="stable-chat-root">
      <FullScreenContainer
        key="stable-fullscreen-container"
        role="main"
        aria-label="Virtual Building Assistant"
      >
        <ContentContainer>
          <Header
            title={data.propertyName || undefined}
            onOpenSidebar={() => uiState.setIsSidebarOpen(true)}
            onBack={onBack}
          >
            {data.showProgressBar && (
              <CompactProgressBar
                key="progress-bar-persistent"
                priorityCards={data.priorityCards}
                completedCards={data.completedCards}
                shouldAnimate={!uiState.hasProgressBarAnimated.current}
                onAnimationComplete={uiState.handleProgressBarAnimationComplete}
                showTasksButton={true}
                onTasksButtonClick={() => uiState.setIsSidebarOpen(true)}
              />
            )}
          </Header>
          <Box
            key="stable-content-container"
            flex="1"
            display="flex"
            padding="none"
            minHeight="0"
            width="100%"
            position="relative"
            minWidth="100%"
          >
            <ChatReadinessGate
              key="stable-chat-interface"
              uiState={uiState}
              data={data}
              stableHandleSendMessageWithTracking={
                stableHandleSendMessageWithTracking
              }
              stableOnTypewriterDone={stableOnTypewriterDone}
            />
          </Box>
          {uiState.showVoice && (
            <VoiceAgentScreen onClose={() => uiState.setShowVoice(false)} />
          )}
        </ContentContainer>
      </FullScreenContainer>
      <GlassEffect>
        <GlassEffectBackground />
      </GlassEffect>
      <ChatBackground />
      <TasksSidebar
        isOpen={uiState.isSidebarOpen}
        onClose={handleSidebarClose}
        onNewConversation={data.startNewConversation}
        onSelectTask={handleSelectTask}
        priorityCards={data.priorityCards}
        completedCards={data.completedCards}
        currentMoveDate={data.currentMoveDate}
      />
    </Chat>
  );
});

export default ChatComponent;
